import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import CircleMessageGroup from './circle_message_group';
import Loader from '../loader';
import useInboxChannel from '../../hooks/useInboxChannel';

// サークル一覧（メッセージ）コンポーネント
//
// TGW-1033: 既存の周期ポーリングを撤廃し、MessageInboxChannel 経由の
// WebSocket でリアルタイム更新する。
function CircleMessageGroupList(props) {
  const { circleId, isCompany } = props;

  const initialData = {
    myCircles: [],
    hasUnreadMessage: {},
    unreadMessageCount: {},
    lastMessages: {},
    pagenationSettings: {
      currentPage: 0,
      maxPerPage: 0,
      isLastPage: false,
    },
  };

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(initialData);

  const loadCircleMessageGroupListFromServer = useCallback(() => {
    return new Promise((resolve) => {
      $.ajax({
        url: '/api/message/my_circle_list',
        dataType: 'json',
        cache: false,
        success: (resp) => {
          setLoading(false);
          setData((prev) => ({
            ...prev,
            ...resp,
            unreadMessageCount: resp.unreadMessageCount || {},
            lastMessages: resp.lastMessages || {},
          }));
          resolve();
        },
        error: () => {
          resolve();
        },
      });
    });
  }, []);

  const loadMoreCircleMessageGroupListFromServer = useCallback((page) => {
    $.ajax({
      url: '/api/message/my_circle_list',
      dataType: 'json',
      data: { page: page + 1 },
      cache: false,
      success: (resp) => {
        setLoading(false);
        setData((prev) => {
          const currentCircleIds = prev.myCircles.map((c) => c.id);
          const merged = prev.myCircles.concat(
            (resp.myCircles || []).filter(
              (c) => !currentCircleIds.includes(c.id),
            ),
          );
          return {
            ...prev,
            myCircles: merged,
            unreadMessageCount: {
              ...(prev.unreadMessageCount || {}),
              ...(resp.unreadMessageCount || {}),
            },
            lastMessages: {
              ...(prev.lastMessages || {}),
              ...(resp.lastMessages || {}),
            },
            pagenationSettings: resp.pagenationSettings,
          };
        });
      },
      error: () => {},
    });
  }, []);

  useEffect(() => {
    loadCircleMessageGroupListFromServer();
  }, [loadCircleMessageGroupListFromServer]);

  // F1〜F3: 既存サークルへの新着 → 先頭に移動 + プレビュー更新 + バッジ +1
  const onMessageReceived = useCallback((payload) => {
    setData((prev) => {
      const targetId = payload.target_id;
      const idx = prev.myCircles.findIndex((c) => c.id === targetId);
      if (idx < 0) return prev;
      const circle = prev.myCircles[idx];
      const reordered = [
        circle,
        ...prev.myCircles.slice(0, idx),
        ...prev.myCircles.slice(idx + 1),
      ];

      const prevLast = (prev.lastMessages || {})[targetId] || {};
      const nextLast = {
        ...prevLast,
        body: payload.last_message_preview,
        created_at: payload.last_message_at,
        sender_id: payload.last_sender_id,
      };

      const isSelfSend = (payload.unread_count_delta || 0) === 0;
      const prevUnread = (prev.unreadMessageCount || {})[targetId] || 0;
      const nextUnread = isSelfSend
        ? prevUnread
        : prevUnread + (payload.unread_count_delta || 1);

      return {
        ...prev,
        myCircles: reordered,
        lastMessages: { ...(prev.lastMessages || {}), [targetId]: nextLast },
        unreadMessageCount: {
          ...(prev.unreadMessageCount || {}),
          [targetId]: nextUnread,
        },
      };
    });
  }, []);

  // F4: 既読化 → unreadCount を 0 にする
  const onUnreadCountChanged = useCallback((payload) => {
    setData((prev) => {
      const targetId = payload.target_id;
      if (!(prev.unreadMessageCount || {})[targetId]) return prev;
      return {
        ...prev,
        unreadMessageCount: {
          ...(prev.unreadMessageCount || {}),
          [targetId]: payload.unread_count || 0,
        },
      };
    });
  }, []);

  // F6: 新規スレッド → サークルが既に一覧にいればスキップ、いなければ先頭追加
  const onNewThread = useCallback((payload) => {
    setData((prev) => {
      const targetId = payload.target_id;
      const exists = prev.myCircles.some((c) => c.id === targetId);
      if (exists) return prev;
      const group = payload.message_group;
      if (!group) return prev;
      const isSelfSend = (payload.unread_count_delta || 0) === 0;
      return {
        ...prev,
        myCircles: [group, ...prev.myCircles],
        unreadMessageCount: {
          ...(prev.unreadMessageCount || {}),
          [targetId]: isSelfSend ? 0 : payload.unread_count_delta || 1,
        },
        lastMessages: {
          ...(prev.lastMessages || {}),
          [targetId]: {
            body: payload.last_message_preview,
            created_at: payload.last_message_at,
            sender_id: payload.last_sender_id,
          },
        },
      };
    });
  }, []);

  useInboxChannel({
    currentUserId: data.currentUserId,
    companyId: null,
    scope: 'circle',
    onMessageReceived,
    onUnreadCountChanged,
    onNewThread,
    refetch: loadCircleMessageGroupListFromServer,
  });

  const currentCircleId = circleId;
  const lastCircle = data.myCircles[data.myCircles.length - 1];
  const isLastPage = data.pagenationSettings.isLastPage;
  const currentPage = data.pagenationSettings.currentPage;

  const circleMessageGroup = data.myCircles.map((circle) => {
    return (
      <CircleMessageGroup
        circle={circle}
        currentCircleId={currentCircleId}
        hasUnreadMessage={false}
        key={circle.id}
        isCompany={isCompany}
        isLast={lastCircle == circle}
        unreadMessageCount={(data.unreadMessageCount || {})[circle.id]}
        lastMessage={(data.lastMessages || {})[circle.id]}
      />
    );
  });

  let circleMessageGroupList = (
    <p>
      <span className="txt-member">所属サークルがありません</span>
    </p>
  );
  const appLinkButton = isLastPage ? (
    ''
  ) : (
    <li
      onClick={() => loadMoreCircleMessageGroupListFromServer(currentPage)}
      className="hov--default"
    >
      <div className="row justify-content-around align-items-center pt-3 pb-3 border-top">
        もっとみる
      </div>
    </li>
  );
  if (data.myCircles) {
    circleMessageGroupList = (
      <ul className="list-unstyled mb-0">
        {circleMessageGroup}
        {appLinkButton}
      </ul>
    );
  }

  return (
    data.myCircles.length > 0 && (
      <div>
        <div className="container col-md-8 col-lg-6">
          <h6 className="h6--list-title text-fz-14px text-black-50 pt-2">
            所属サークルのメッセージ
          </h6>
        </div>
        <div className="bg-white h-100 mb-2">
          <div className="container col-md-8 col-lg-6 h-100">
            <Loader isActive={loading} />
            {circleMessageGroupList}
          </div>
        </div>
      </div>
    )
  );
}

CircleMessageGroupList.displayName = 'Root';
CircleMessageGroupList.propTypes = {
  circleId: PropTypes.number,
  isCompany: PropTypes.bool,
};

export default CircleMessageGroupList;
